import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import apiClient from "@/utils/apiClient";
import {
  Play, Tv, Radio, ChevronRight, PlayCircle,
  Music, Video, ExternalLink
} from "lucide-react";

const ADDITIONAL_CONTENT = [
  {
    id: "smahoobits",
    title: "SMAHooBITs Wellness Guide",
    description: "Adaptive.TT Life Stressors self-care hygiene tips and resources",
    thumbnail: "https://images.unsplash.com/photo-1598550480917-1c485268676e?w=400",
    type: "playlist",
    episodes: 24
  },
  {
    id: "laughing-sessions",
    title: "The Laughing Therapist Sessions",
    description: "Laughter therapy sessions for mental health and emotional balance",
    thumbnail: "https://images.unsplash.com/photo-1694299352873-0c29d862e87a?w=400",
    type: "series",
    episodes: 12
  },
  {
    id: "mindset-reset",
    title: "MindSET REset Episodes",
    description: "Weekly episodes on behavioral change and stress management",
    thumbnail: "https://images.pexels.com/photos/12994733/pexels-photo-12994733.png?auto=compress&cs=tinysrgb&w=400",
    type: "series",
    episodes: 8
  },
  {
    id: "faith-wellness",
    title: "Faith-Centered Wellness",
    description: "Spiritually-aligned approaches to mental health",
    thumbnail: "https://images.unsplash.com/photo-1615134732800-ca7ef7a3388c?w=400",
    type: "podcast",
    episodes: 15
  }
];

const TYPE_COLORS = {
  playlist: 'bg-spa-sage text-white',
  series: 'bg-spa-sky text-white',
  podcast: 'bg-spa-clay text-white'
};

export default function Streaming() {
  const [content, setContent] = useState([]);
  const [media, setMedia] = useState([]);
  const [activeStream, setActiveStream] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  const fetchContent = useCallback(async () => {
    try {
      const response = await apiClient.get('/content/streaming');
      setContent(response.data);
    } catch (error) { /* silent */ }
  }, []);

  const fetchMedia = useCallback(async () => {
    try {
      const response = await apiClient.get('/media/content');
      setMedia(response.data.items || []);
    } catch (error) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchContent();
    fetchMedia();
  }, [fetchContent, fetchMedia]);

  const youtubeMedia = media.filter(m => m.platform === "youtube");
  const spotifyMedia = media.filter(m => m.platform === "spotify");

  const tabs = [
    { id: "all", label: "All Content" },
    { id: "youtube", label: "YouTube", icon: Video },
    { id: "spotify", label: "Spotify", icon: Music },
    { id: "ondemand", label: "On-Demand", icon: Play },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="streaming-page">
        {/* Header */}
        <div>
          <p className="overline-text mb-2">SMAHo.OS Streaming Services</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss mb-3">
            Rice And Peas For The Soul TV
          </h1>
          <p className="text-spa-text-secondary max-w-2xl">
            Access wellness streaming content including podcasting, SMAHoody, and
            RAPsoul.TV online streaming services for your mental and emotional wellbeing.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
                activeTab === t.id
                  ? "bg-spa-terracotta text-white"
                  : "bg-spa-muted text-spa-text-secondary hover:bg-spa-sage/10"
              }`}
              data-testid={`tab-${t.id}`}
            >
              {t.icon && <t.icon className="w-4 h-4" />}
              {t.label}
            </button>
          ))}
        </div>

        {/* Featured: Rice And Peas For The Soul Online TV */}
        {(activeTab === "all" || activeTab === "youtube") && (
          <div className="spa-card" data-testid="featured-rap-tv">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-sm">
                <Play className="w-5 h-5 text-white fill-white" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-spa-moss">Rice And Peas For The Soul Online TV</h3>
                <p className="text-xs text-spa-text-secondary">Featured wellness streaming from Mr. RAPsoul.R</p>
              </div>
            </div>
            <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingBottom: "56.25%" }}>
              <iframe
                className="absolute inset-0 w-full h-full rounded-2xl"
                src="https://www.youtube.com/embed/iUuVXNIYhjE?si=PvK03b8OdwHN7doM"
                title="Rice And Peas For The Soul Online TV"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
            <div className="mt-3 text-center">
              <a
                href="https://www.youtube.com/@rapSOULrTV"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-spa-terracotta hover:underline font-medium"
                data-testid="streaming-channel-link"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Visit RAPsoul.R Online TV YouTube Channel
              </a>
            </div>
          </div>
        )}

        {/* YouTube Section */}
        {(activeTab === "all" || activeTab === "youtube") && youtubeMedia.length > 0 && (
          <section data-testid="youtube-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <Video className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="font-serif text-2xl text-spa-moss">YouTube Videos</h2>
                <p className="text-spa-text-secondary text-sm">Curated wellness video content</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {youtubeMedia.map(item => (
                <div key={item.media_id} className="spa-card overflow-hidden group" data-testid={`yt-${item.media_id}`}>
                  <div className="aspect-video -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-2xl">
                    <iframe
                      src={item.embed_url}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={item.title}
                    />
                  </div>
                  <h3 className="font-serif text-lg text-spa-moss mb-1">{item.title}</h3>
                  <p className="text-sm text-spa-text-secondary line-clamp-2">{item.description}</p>
                  <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600">{item.category}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Spotify Section */}
        {(activeTab === "all" || activeTab === "spotify") && spotifyMedia.length > 0 && (
          <section data-testid="spotify-section">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Music className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="font-serif text-2xl text-spa-moss">Spotify Playlists</h2>
                <p className="text-spa-text-secondary text-sm">Curated wellness audio & meditation</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {spotifyMedia.map(item => (
                <div key={item.media_id} className="spa-card overflow-hidden" data-testid={`sp-${item.media_id}`}>
                  <iframe
                    src={item.embed_url}
                    className="w-full h-[352px] rounded-xl -mx-0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    title={item.title}
                  />
                  <h3 className="font-serif text-lg text-spa-moss mt-3 mb-1">{item.title}</h3>
                  <p className="text-sm text-spa-text-secondary line-clamp-2">{item.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Live/Featured Stream Player */}
        {(activeTab === "all" || activeTab === "ondemand") && (
          <section>
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="spa-card overflow-hidden" data-testid="main-player">
                  <div className="relative aspect-video bg-spa-moss rounded-xl overflow-hidden">
                    {activeStream ? (
                      <iframe
                        src={activeStream.player_url}
                        className="w-full h-full"
                        allow="autoplay; fullscreen"
                        allowFullScreen
                        title={activeStream.title}
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mb-4">
                          <Tv className="w-10 h-10" strokeWidth={1.5} />
                        </div>
                        <h3 className="font-serif text-xl mb-2">Welcome to SMAHOO TV</h3>
                        <p className="text-white/70 text-sm">Select a stream to begin watching</p>
                      </div>
                    )}
                  </div>
                  {activeStream && (
                    <div className="mt-4">
                      <h2 className="font-serif text-xl text-spa-moss">{activeStream.title}</h2>
                      <p className="text-spa-text-secondary text-sm mt-1">{activeStream.description}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-serif text-lg text-spa-moss">Live Channels</h3>
                {content.map((stream) => (
                  <button
                    key={stream.id}
                    onClick={() => setActiveStream(stream)}
                    className={`w-full spa-card p-4 text-left transition-all ${
                      activeStream?.id === stream.id ? 'ring-2 ring-spa-terracotta' : ''
                    }`}
                    data-testid={`stream-${stream.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="relative w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={stream.thumbnail} alt={stream.title} className="w-full h-full object-cover" />
                        {stream.type === 'live' && (
                          <span className="absolute top-1 left-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded">LIVE</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-spa-moss text-sm truncate">{stream.title}</h4>
                        <p className="text-xs text-spa-text-secondary truncate">{stream.description}</p>
                      </div>
                      <PlayCircle className="w-6 h-6 text-spa-terracotta flex-shrink-0" />
                    </div>
                  </button>
                ))}
                {content.length === 0 && (
                  <div className="text-center py-8 text-spa-text-secondary">
                    <Radio className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No live streams at the moment</p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* On-Demand Content */}
        {(activeTab === "all" || activeTab === "ondemand") && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-spa-moss">On-Demand Content</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {ADDITIONAL_CONTENT.map((item) => (
                <div key={item.id} className="spa-card overflow-hidden group cursor-pointer" data-testid={`content-${item.id}`}>
                  <div className="relative h-40 -mx-6 -mt-6 mb-4 overflow-hidden">
                    <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <button className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-spa-terracotta ml-1" fill="currentColor" />
                      </div>
                    </button>
                    <div className="absolute top-3 right-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${TYPE_COLORS[item.type] || TYPE_COLORS.playlist}`}>{item.type}</span>
                    </div>
                  </div>
                  <h3 className="font-serif text-lg text-spa-moss mb-1">{item.title}</h3>
                  <p className="text-sm text-spa-text-secondary line-clamp-2 mb-2">{item.description}</p>
                  <p className="text-xs text-spa-terracotta">{item.episodes} episodes</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Featured Banner */}
        <div className="spa-card bg-gradient-to-r from-spa-moss to-spa-moss/80 text-white overflow-hidden">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-white/70 text-sm mb-2">Featured Show</p>
              <h2 className="font-serif text-2xl mb-2">Rice And Peas For The Soul REVISITED</h2>
              <p className="text-white/80">
                The flagship wellness show by Mr. RAPsoul.R, featuring faith-centered
                approaches to mental health and behavioral change.
              </p>
            </div>
            <button className="spa-button-primary whitespace-nowrap bg-white text-spa-moss hover:bg-white/90">
              <Play className="w-4 h-4 mr-2" /> Watch Now
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
